/**
 * SmokeLayerProvider
 *
 * Renders active NWT prefires ("smokes") from GeoServer as a live layer
 * with FontAwesome smoke-cloud markers. Backing feature type:
 * `intellifire:smokes` (WFS GeoJSON). Active-only filtering is currently
 * applied at the GeoServer layer level via CQL; collector-side filtering
 * is tracked by #584.
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayerProvider } from '../BaseLayerProvider';
import { applyOpacityToMarkers } from '../markerOpacity';
import {
  LayerDataConfig,
  LayerProcessingConfig,
  LayerRenderingConfig,
  LayerOptions,
} from '../interfaces';
import { logger } from '../../../utils/logger';
import { getBackendUrl } from '../../../services/configService';

const log = logger('SmokeLayerProvider');

declare global {
  interface Window {
    smokeMarkers?: mapboxgl.Marker[];
  }
}

// Visual constants for the smoke marker — dark grey to suggest smoke,
// sized for visibility at typical operational zoom levels without
// crowding fire markers nearby.
const SMOKE_ICON_CLASS = 'fa-solid fa-smog';
const SMOKE_ICON_COLOR = '#5a5a5a';
const SMOKE_ICON_SIZE_PX = 22;

/**
 * The intellifire:smokes feature type does not currently expose a
 * top-level `fire_number` column — the human-readable prefire number
 * (e.g. "ZF007-26P") only lives inside the `data` jsonb at `.number`.
 * Parse it defensively: `data` may arrive as a JSON string (GeoServer's
 * usual behaviour for jsonb) or as an already-parsed object, and may
 * be missing entirely on older rows.
 *
 * Tracked by the smokes-schema follow-up; this is a frontend workaround
 * until the harvester surfaces the column natively.
 */
export function extractFireNumber(props: Record<string, unknown>): string | null {
  const direct = props.fire_number ?? props.number;
  if (typeof direct === 'string' && direct.trim()) return direct;
  const raw = props.data;
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (parsed && typeof parsed === 'object' && 'number' in parsed) {
    const n = (parsed as { number: unknown }).number;
    if (typeof n === 'string' && n.trim()) return n;
  }
  return null;
}

function formatPopupHtml(props: Record<string, unknown>): string {
  const fireNumber = extractFireNumber(props);
  const lines = [`<h3>Active Prefire (Smoke)</h3>`];
  if (fireNumber) {
    lines.push(`<b>Fire number:</b> ${fireNumber}`);
  }
  if (props.fire_id) lines.push(`<b>Fire ID:</b> ${props.fire_id}`);
  if (props.fire_year) lines.push(`<b>Year:</b> ${props.fire_year}`);
  if (props.region) lines.push(`<b>Region:</b> ${props.region}`);
  if (props.wildfire_status) lines.push(`<b>Status:</b> ${props.wildfire_status}`);
  if (props.estimated_size != null) lines.push(`<b>Estimated size:</b> ${props.estimated_size} ha`);
  if (props.discovery_size != null) lines.push(`<b>Discovery size:</b> ${props.discovery_size} ha`);
  if (props.estimated_start_date) lines.push(`<b>Start date:</b> ${props.estimated_start_date}`);
  if (props.last_seen) lines.push(`<b>Last seen:</b> ${props.last_seen}`);
  return lines.join('<br />');
}

function clearSmokeMarkers(): void {
  if (window.smokeMarkers && window.smokeMarkers.length) {
    window.smokeMarkers.forEach((m) => m.remove());
    window.smokeMarkers = [];
  } else {
    window.smokeMarkers = [];
  }
  document.querySelectorAll('.mapboxgl-popup.smoke-popup').forEach((p) => p.remove());
}

export class SmokeLayerProvider extends BaseLayerProvider {
  constructor() {
    const BACKEND_URL = getBackendUrl();

    const dataConfig: LayerDataConfig = {
      name: 'Live Smokes',
      shortName: 'LiveSmokes',
      mainIcon: SMOKE_ICON_CLASS,
      sources: {
        smokes: {
          url:
            `${BACKEND_URL}/api/geoserver/geoserver/wfs` +
            `?service=WFS&version=2.0.0&request=GetFeature` +
            `&typeName=intellifire:smokes&outputFormat=application%2Fjson`,
          type: 'geojson',
        },
      },
      propertyNameMapping: {
        fire_id: 'Fire ID',
        fire_year: 'Year',
        region: 'Region',
        wildfire_status: 'Status',
        estimated_size: 'Estimated size (ha)',
        discovery_size: 'Discovery size (ha)',
        estimated_start_date: 'Start date',
        last_seen: 'Last seen',
      },
    };

    const processingConfig: LayerProcessingConfig = {
      mainSource: {
        sourceName: 'smokes',
        idKey: 'fire_id',
      },
    };

    const renderingConfig: LayerRenderingConfig = {
      // Zero-radius circle to satisfy the styleFunction contract — the
      // visible marker is the DOM element created in markerFunction.
      styleFunction: () =>
        ({
          type: 'circle',
          paint: {
            'circle-radius': 0,
            'circle-opacity': 0,
          },
        }) as mapboxgl.CircleLayerSpecification,

      markerFunction: (map: mapboxgl.Map, data: GeoJSON.FeatureCollection) => {
        clearSmokeMarkers();
        if (!data || !data.features) return;
        log.debug(`Rendering ${data.features.length} smoke markers`);

        data.features.forEach((feature: GeoJSON.Feature) => {
          if (!feature.geometry || feature.geometry.type !== 'Point') return;
          const props = (feature.properties ?? {}) as Record<string, unknown>;
          const coords = feature.geometry.coordinates as [number, number];

          const el = document.createElement('div');
          el.className = 'smoke-marker';
          el.style.cursor = 'pointer';
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'center';
          el.style.width = `${SMOKE_ICON_SIZE_PX}px`;
          el.style.height = `${SMOKE_ICON_SIZE_PX}px`;
          el.innerHTML =
            `<i class="${SMOKE_ICON_CLASS}" ` +
            `style="font-size:${SMOKE_ICON_SIZE_PX}px;color:${SMOKE_ICON_COLOR};` +
            `text-shadow:0 0 2px rgba(255,255,255,0.85);" ` +
            `aria-hidden="true"></i>`;

          const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
            .setLngLat(coords)
            .addTo(map);

          el.addEventListener('mouseenter', () => {
            const label =
              extractFireNumber(props) ??
              (typeof props.fire_id === 'string' ? props.fire_id : 'Prefire');
            const hover = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              className: 'hover-popup smoke-hover-popup',
              offset: 12,
            })
              .setLngLat(coords)
              .setHTML(`Smoke — ${label}`)
              .addTo(map);
            (el as unknown as { _hoverPopup?: mapboxgl.Popup })._hoverPopup = hover;
          });
          el.addEventListener('mouseleave', () => {
            const ref = el as unknown as { _hoverPopup?: mapboxgl.Popup | null };
            if (ref._hoverPopup) {
              ref._hoverPopup.remove();
              ref._hoverPopup = null;
            }
          });

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            document
              .querySelectorAll('.mapboxgl-popup.click-popup')
              .forEach((p) => p.remove());
            new mapboxgl.Popup({
              closeButton: true,
              closeOnClick: true,
              className: 'click-popup smoke-popup',
              maxWidth: '300px',
              offset: 18,
            })
              .setLngLat(coords)
              .setHTML(formatPopupHtml(props))
              .addTo(map);
          });

          window.smokeMarkers = window.smokeMarkers || [];
          window.smokeMarkers.push(marker);
        });

        log.debug(`Created ${window.smokeMarkers?.length ?? 0} smoke markers`);
      },

      hoverPopupFunction: () => null,
      clickPopupFunction: () => null,

      cleanupFunction: () => {
        clearSmokeMarkers();
      },
    };

    const options: LayerOptions = {
      autoRefresh: true,
      refreshInterval: 300000, // 5 minutes
      controlPosition: 'top-left',
    };

    super(dataConfig, processingConfig, renderingConfig, options);
  }

  /** #606 — dim the HTML smoke markers (paint properties don't reach them). */
  setOpacity(opacity: number): void {
    applyOpacityToMarkers(window.smokeMarkers, opacity);
  }
}
